import React from 'react';
import { Shield, Award, Beaker, Factory, MapPin, Calendar, CheckCircle, Zap, Star } from 'lucide-react';

const PPF = () => {
  const timelineEvents = [
    {
      year: '2021',
      month: 'January',
      title: 'Research & Development Begins',
      description: 'Started intensive research on Indian climate conditions, UV exposure patterns, and automotive paint challenges specific to Indian roads and weather.',
      icon: Beaker
    },
    {
      year: '2021',
      month: 'March',
      title: 'Laboratory Testing Phase',
      description: 'Comprehensive testing for extreme temperatures (45°C+), monsoon conditions, dust resistance, and UV degradation specific to Indian subcontinent.',
      icon: Factory
    },
    {
      year: '2021',
      month: 'August',
      title: 'Field Testing & Validation',
      description: 'Real-world testing across different Indian cities - Mumbai (humidity), Delhi (pollution), Chennai (heat), Bangalore (weather variations).',
      icon: MapPin
    },
    {
      year: '2022',
      month: 'January',
      title: 'Korean Manufacturing Partnership',
      description: 'Partnered with leading Korean manufacturers to produce PPF using world-class technology. Established OEM supply relationships with global automotive brands.',
      icon: Award
    },
    {
      year: '2022',
      month: 'March',
      title: 'Official Launch',
      description: 'Successfully launched our Indian-specific PPF with 7-year warranty, setting new standards for paint protection in the Indian automotive market.',
      icon: Shield
    }
  ];

  const indianConditions = [
    {
      condition: 'Extreme Heat (45°C+)',
      solution: 'Enhanced thermal stability',
      icon: '🌡️'
    },
    {
      condition: 'Heavy Monsoons',
      solution: 'Superior water resistance',
      icon: '🌧️'
    },
    {
      condition: 'Dust & Pollution',
      solution: 'Anti-static properties',
      icon: '🌪️'
    },
    {
      condition: 'Intense UV Radiation',
      solution: 'UV-blocking technology',
      icon: '☀️'
    },
    {
      condition: 'Road Salt & Chemicals',
      solution: 'Chemical resistance formula',
      icon: '🧪'
    },
    {
      condition: 'Traffic Debris',
      solution: 'Impact-resistant coating',
      icon: '🚗'
    }
  ];

  const features = [
    {
      title: 'Self-Healing Technology',
      description: 'Minor scratches disappear with heat activation, perfect for Indian traffic conditions',
      icon: Zap
    },
    {
      title: 'Optical Clarity',
      description: 'Maintains crystal-clear transparency even after years of exposure to Indian sun',
      icon: Shield
    },
    {
      title: 'Easy Maintenance',
      description: 'Designed for Indian car owners with minimal maintenance requirements',
      icon: CheckCircle
    },
    {
      title: 'Comprehensive Warranty',
      description: 'Two warranty options to suit your protection needs and budget',
      icon: Award
    }
  ];

  const warrantyTypes = [
    {
      name: 'UniGuard Pro',
      warranty: '7 Years',
      description: 'Premium protection with comprehensive coverage for most Indian driving conditions',
      features: [
        'Self-healing technology',
        'UV protection coating',
        'Hydrophobic surface treatment',
        'Rock chip resistance',
        '150 microns thickness',
        'Easy maintenance formula',
        'Professional installation',
        'Annual inspection included'
      ],
      idealFor: 'Daily drivers, city commuting, moderate highway use',
      coverage: 'Cracking, peeling, adhesive failure, edge lifting, discoloration',
      price: 'Starting from ₹45,000',
      popular: true
    },
    {
      name: 'UniGuard Ultra',
      warranty: '12 Years',
      description: 'Ultimate protection for luxury vehicles and extreme Indian conditions',
      features: [
        'Advanced self-healing technology',
        'Enhanced UV protection',
        'Superior hydrophobic coating',
        'Maximum rock chip resistance',
        'Anti-static dust repulsion',
        '200 microns thickness',
        'Premium installation process',
        'Bi-annual inspection included',
        'Extended coverage guarantee'
      ],
      idealFor: 'Luxury vehicles, frequent highway travel, extreme weather conditions',
      coverage: 'Cracking, peeling, adhesive failure, edge lifting, discoloration',
      price: 'Starting from ₹75,000',
      premium: true
    }
  ];

  return (
    <div className="min-h-screen bg-white pt-24">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-6">
            <div className="inline-flex items-center px-4 py-2 bg-blue-700/50 rounded-full text-sm font-medium">
              <Calendar className="w-4 h-4 mr-2" />
              Developed 2021 • Launched 2022
            </div>
            <h1 className="text-4xl lg:text-6xl font-bold leading-tight">
              Indian-Engineered
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-white">
                Paint Protection Film
              </span>
            </h1>
            <p className="text-xl lg:text-2xl text-blue-100 max-w-4xl mx-auto leading-relaxed">
              The first Paint Protection Film specifically designed and tested for Indian road and climate conditions. 
              Manufactured in Korea using world-class technology and trusted by leading automotive OEMs globally.
              Two years of intensive development, engineered for excellence. Available in UniGuard Pro (7-year) and UniGuard Ultra (12-year) warranty options.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-6">
              <button className="bg-white text-blue-900 px-8 py-4 rounded-lg font-semibold hover:bg-blue-50 transition-all duration-200 shadow-lg">
                Learn About Our PPF
              </button>
              <button className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-blue-900 transition-all duration-200">
                View Installation Process
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Development Story */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Our Development Journey
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Two years of dedicated research, development, and testing to create the perfect PPF for Indian conditions
            </p>
          </div>

          {/* Timeline */}
          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 bg-blue-600 h-full hidden lg:block"></div>
            
            {timelineEvents.map((event, index) => {
              const IconComponent = event.icon;
              const isEven = index % 2 === 0;
              
              return (
                <div key={index} className={`flex items-center mb-12 ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}>
                  {/* Content */}
                  <div className={`w-full lg:w-5/12 ${isEven ? 'lg:pr-8 lg:text-right' : 'lg:pl-8 lg:text-left'}`}>
                    <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200 hover:shadow-xl transition-shadow">
                      <div className="flex items-center mb-3">
                        <div className={`w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mr-4 ${!isEven ? 'lg:order-2 lg:ml-4 lg:mr-0' : ''}`}>
                          <IconComponent className="w-6 h-6 text-white" />
                        </div>
                        <div className={!isEven ? 'lg:order-1' : ''}>
                          <div className="text-sm font-semibold text-blue-600">{event.month} {event.year}</div>
                          <h3 className="text-lg font-bold text-gray-900">{event.title}</h3>
                        </div>
                      </div>
                      <p className="text-gray-600 leading-relaxed">{event.description}</p>
                    </div>
                  </div>
                  
                  {/* Timeline dot - hidden on mobile, visible on lg screens */}
                  <div className="hidden lg:block w-2/12 flex justify-center">
                    <div className="w-4 h-4 bg-blue-600 rounded-full border-4 border-white shadow-lg"></div>
                  </div>
                  
                  {/* Empty space for layout balance */}
                  <div className="hidden lg:block w-5/12"></div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Korean Manufacturing & OEM Story */}
      <section className="py-20 bg-gradient-to-r from-slate-900 via-blue-900 to-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              Korean Excellence,
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-white"> Global Trust</span>
            </h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Manufactured in South Korea using world-class technology and trusted by leading automotive brands worldwide
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Manufacturing Story */}
            <div className="space-y-6">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mr-4">
                  <Factory className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold">Korean Manufacturing Excellence</h3>
              </div>
              
              <p className="text-lg text-blue-100 leading-relaxed">
                Our Paint Protection Film is proudly manufactured in <strong>South Korea</strong>, a global leader in advanced materials and automotive technology. Using state-of-the-art facilities and precision engineering, we leverage Korea's decades of expertise in polymer science and nanotechnology.
              </p>
              
              <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                <h4 className="text-lg font-semibold text-white mb-3">Korean Advantages:</h4>
                <ul className="space-y-2">
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                    <span className="text-blue-100">Advanced polymer technology</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                    <span className="text-blue-100">Precision manufacturing processes</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                    <span className="text-blue-100">Stringent quality control standards</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                    <span className="text-blue-100">Innovative nanotechnology integration</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* OEM Partnerships */}
            <div className="space-y-6">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center mr-4">
                  <Award className="w-6 h-6 text-gray-900" />
                </div>
                <h3 className="text-2xl font-bold">Trusted by Global Leaders</h3>
              </div>
              
              <p className="text-lg text-blue-100 leading-relaxed">
                As an <strong>Original Equipment Manufacturer (OEM)</strong> supplier, our PPF technology is trusted by leading automotive brands worldwide. This partnership validates our commitment to quality and innovation.
              </p>
              
              <div className="bg-amber-500/10 rounded-xl p-6 border border-amber-500/30">
                <h4 className="text-lg font-semibold text-amber-400 mb-3">OEM Partnerships Include:</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-slate-800/50 rounded-lg">
                    <div className="text-2xl mb-2">🏆</div>
                    <div className="text-sm text-blue-100">Luxury Vehicle Manufacturers</div>
                  </div>
                  <div className="text-center p-3 bg-slate-800/50 rounded-lg">
                    <div className="text-2xl mb-2">🌍</div>
                    <div className="text-sm text-blue-100">International Automotive Brands</div>
                  </div>
                  <div className="text-center p-3 bg-slate-800/50 rounded-lg">
                    <div className="text-2xl mb-2">🔧</div>
                    <div className="text-sm text-blue-100">Premium Aftermarket Solutions</div>
                  </div>
                  <div className="text-center p-3 bg-slate-800/50 rounded-lg">
                    <div className="text-2xl mb-2">⚡</div>
                    <div className="text-sm text-blue-100">High-Performance Applications</div>
                  </div>
                </div>
              </div>

              <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                <h4 className="text-lg font-semibold text-white mb-3">Global Recognition:</h4>
                <ul className="space-y-2">
                  <li className="flex items-center">
                    <Star className="w-5 h-5 text-amber-400 mr-3" />
                    <span className="text-blue-100">ISO 9001:2015 certified manufacturing</span>
                  </li>
                  <li className="flex items-center">
                    <Star className="w-5 h-5 text-amber-400 mr-3" />
                    <span className="text-blue-100">Automotive grade quality standards</span>
                  </li>
                  <li className="flex items-center">
                    <Star className="w-5 h-5 text-amber-400 mr-3" />
                    <span className="text-blue-100">Trusted by 50+ global automotive brands</span>
                  </li>
                  <li className="flex items-center">
                    <Star className="w-5 h-5 text-amber-400 mr-3" />
                    <span className="text-blue-100">Proven in 80+ countries worldwide</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Bottom CTA */}
          <div className="text-center mt-16">
            <div className="bg-slate-800/50 rounded-2xl p-8 border border-slate-700">
              <h3 className="text-2xl font-bold text-white mb-4">
                World-Class Manufacturing, Indian-Engineered Solutions
              </h3>
              <p className="text-lg text-blue-100 mb-6">
                The perfect combination of Korean manufacturing excellence and Indian market expertise, 
                delivering superior protection for your vehicle.
              </p>
              <div className="flex flex-wrap justify-center gap-8 text-center">
                <div>
                  <div className="text-3xl font-bold text-amber-400">🇰🇷</div>
                  <div className="text-sm text-blue-100 mt-2">Korean Technology</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-amber-400">🏭</div>
                  <div className="text-sm text-blue-100 mt-2">OEM Quality</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-amber-400">🇮🇳</div>
                  <div className="text-sm text-blue-100 mt-2">Indian Engineering</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-amber-400">🌍</div>
                  <div className="text-sm text-blue-100 mt-2">Global Standards</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Production Process */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Production Excellence
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              State-of-the-art manufacturing process with rigorous quality control and testing
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Laboratory Testing */}
            <div className="space-y-6">
              <div className="relative rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src="https://images.unsplash.com/photo-1646956141183-c993158094d2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwyfHxsYWJvcmF0b3J5JTIwdGVzdGluZ3xlbnwwfHx8fDE3NTg4NTc4MDN8MA&ixlib=rb-4.1.0&q=85"
                  alt="Laboratory Testing Process"
                  className="w-full h-64 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4 text-white">
                  <h3 className="text-xl font-bold mb-2">Advanced Laboratory Testing</h3>
                  <p className="text-sm text-gray-200">Rigorous testing for Indian climate conditions</p>
                </div>
              </div>
            </div>

            {/* Production Line */}
            <div className="space-y-6">
              <div className="relative rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_auto-care-site/artifacts/kjsbb17b_IMG_1245.jpeg"
                  alt="PPF Manufacturing Process"
                  className="w-full h-64 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4 text-white">
                  <h3 className="text-xl font-bold mb-2">Automated Production</h3>
                  <p className="text-sm text-gray-200">Advanced machinery creating PPF rolls in manufacturing facility</p>
                </div>
              </div>
            </div>

            {/* Quality Control */}
            <div className="space-y-6">
              <div className="relative rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_auto-care-site/artifacts/1h1xpyz3_IMG_1243.webp"
                  alt="Quality Assurance Process"
                  className="w-full h-64 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4 text-white">
                  <h3 className="text-xl font-bold mb-2">Quality Assurance</h3>
                  <p className="text-sm text-gray-200">Rigorous quality control for every PPF roll</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Indian Conditions */}
      <section className="py-20 bg-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Engineered for Indian Conditions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specially designed to withstand the unique challenges of Indian roads and climate
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {indianConditions.map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                <div className="text-center">
                  <div className="text-4xl mb-4">{item.icon}</div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{item.condition}</h3>
                  <p className="text-blue-600 font-semibold">{item.solution}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Advanced Features
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Cutting-edge technology designed specifically for Indian automotive needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div key={index} className="text-center space-y-4">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                    <IconComponent className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Warranty Types Section */}
      <section className="py-20 bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Choose Your Protection Level
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Two comprehensive warranty options designed specifically for Indian conditions and driving patterns
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {warrantyTypes.map((warranty, index) => (
              <div key={index} className={`relative bg-white rounded-2xl shadow-xl overflow-hidden ${warranty.premium ? 'border-2 border-amber-400' : warranty.popular ? 'border-2 border-blue-500' : 'border border-gray-200'}`}>
                {/* Badge */}
                {warranty.popular && (
                  <div className="absolute top-4 right-4 bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                    Most Popular
                  </div>
                )}
                {warranty.premium && (
                  <div className="absolute top-4 right-4 bg-gradient-to-r from-amber-400 to-orange-500 text-gray-900 px-3 py-1 rounded-full text-sm font-semibold">
                    Premium
                  </div>
                )}

                <div className="p-8">
                  {/* Header */}
                  <div className="text-center mb-8">
                    <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2">
                      {warranty.name}
                    </h3>
                    <div className={`text-4xl lg:text-5xl font-bold mb-2 ${warranty.premium ? 'text-amber-500' : 'text-blue-600'}`}>
                      {warranty.warranty}
                    </div>
                    <p className="text-lg text-gray-600">
                      {warranty.description}
                    </p>
                  </div>

                  {/* Price */}
                  <div className="text-center mb-8">
                    <div className={`text-2xl font-bold ${warranty.premium ? 'text-amber-600' : 'text-blue-600'}`}>
                      {warranty.price}
                    </div>
                    <p className="text-sm text-gray-500 mt-1">*Price varies by vehicle size</p>
                  </div>

                  {/* Features */}
                  <div className="space-y-4 mb-8">
                    <h4 className="font-semibold text-gray-900">What's Included:</h4>
                    <ul className="space-y-3">
                      {warranty.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start">
                          <CheckCircle className={`w-5 h-5 mr-3 mt-0.5 flex-shrink-0 ${warranty.premium ? 'text-amber-500' : 'text-blue-500'}`} />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Ideal For */}
                  <div className="bg-gray-50 rounded-xl p-4 mb-6">
                    <h4 className="font-semibold text-gray-900 mb-2">Ideal For:</h4>
                    <p className="text-gray-700 text-sm">{warranty.idealFor}</p>
                  </div>

                  {/* Coverage */}
                  <div className="mb-8">
                    <h4 className="font-semibold text-gray-900 mb-2">Warranty Coverage:</h4>
                    <p className="text-gray-600 text-sm">{warranty.coverage}</p>
                  </div>

                  {/* CTA Button */}
                  <button className={`w-full py-4 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1 ${
                    warranty.premium 
                      ? 'bg-gradient-to-r from-amber-400 to-orange-500 text-gray-900 hover:from-amber-500 hover:to-orange-600' 
                      : 'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700'
                  }`}>
                    Choose {warranty.name}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Additional Info */}
          <div className="mt-16 text-center">
            <div className="bg-white rounded-xl shadow-lg p-8 max-w-4xl mx-auto">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Why Choose Our Indian-Engineered PPF?
              </h3>
              <div className="grid md:grid-cols-3 gap-6 text-center">
                <div>
                  <div className="text-3xl mb-2">🇮🇳</div>
                  <h4 className="font-semibold text-gray-900 mb-2">Made for India</h4>
                  <p className="text-gray-600 text-sm">Specifically tested and designed for Indian road and climate conditions</p>
                </div>
                <div>
                  <div className="text-3xl mb-2">🔬</div>
                  <h4 className="font-semibold text-gray-900 mb-2">Lab Tested</h4>
                  <p className="text-gray-600 text-sm">2 years of extensive laboratory and field testing across Indian cities</p>
                </div>
                <div>
                  <div className="text-3xl mb-2">✅</div>
                  <h4 className="font-semibold text-gray-900 mb-2">Proven Results</h4>
                  <p className="text-gray-600 text-sm">Thousands of satisfied customers across India with verified performance</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Experience Indian-Engineered Excellence
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Protect your vehicle with PPF designed specifically for Indian conditions. Choose from UniGuard Pro (7-year) or UniGuard Ultra (12-year) warranty options with professional installation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-blue-900 px-8 py-4 rounded-lg font-semibold hover:bg-blue-50 transition-all duration-200 shadow-lg">
              Book PPF Installation
            </button>
            <button className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-blue-900 transition-all duration-200">
              Learn More About Our PPF
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PPF;